import { WEB_COMPONENT_POLICY_PANEL_EXCHANGE_USER_GROUPS_GRID } from '../../../../../../../../webcomponents.constants';
import { FormFieldInput } from '../../../../../../../../ui/gzForm/formField/component/FormFieldInput';
import { html, PropertyValues, TemplateResult } from 'lit';
import {
  SELECTION_STORE_CONFIGURATION,
  SelectionStoreMode,
  ViewType,
} from '../../../../../../../../grid/gridView/types';
import { DEFAULT_PAGE_SIZE } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView/configs/page-config';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { GzImageRepoWebComponent } from '../../../../../../../../ui/gzImageRepo';
import { SelectOption } from '../../../../../../../../ui/gzSelect/types';
import { PolicyGridEvents } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/events/events';
import { ButtonColors, ButtonTypes } from '../../../../../../../../ui/gzButton/types';
import { query, customElement } from 'lit/decorators.js';
import { GridViewWebComponent } from '../../../../../../../../grid/gridView';
import {
  Column,
  ColumnKeys,
  FILTERS,
  getGridColumnDetails,
  GRID_COLUMNS,
  PREDEFINED_FILTERS_KEYS,
  statusCellCustomRenderer,
} from './grid-config';
import { GzExchangeUserGroupsGridDataProvider, UserGroupUiData } from './GzExchangeUserGroupsGridDataProvider';
import { ImageState } from '../../../../../../../../ui/gzImageRepo/types';
import { GzButtonWebComponent } from '../../../../../../../../ui/gzButton';

@customElement(WEB_COMPONENT_POLICY_PANEL_EXCHANGE_USER_GROUPS_GRID)
export class GzExchangeUserGroupsGridWebComponent extends FormFieldInput<string[]> {
  @query('#policy-ui-settings-grid')
  protected gridComponent: GridViewWebComponent<Column, ColumnKeys>;
  @query('#enable-button')
  protected enableButton: GzButtonWebComponent;
  @query('#disable-button')
  protected disableButton: GzButtonWebComponent;

  protected selectedItemsGridData = [];

  getFormValue(): string[] {
    return (this.gridComponent?.dataProvider as GzExchangeUserGroupsGridDataProvider)?.selected || [];
  }

  setFormValue(value: string[]): void {
    (this.gridComponent.dataProvider as GzExchangeUserGroupsGridDataProvider).selected = value;
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    this.buildGrid();
  }

  protected render() {
    return html`<gz-grid-view
      id="policy-ui-settings-grid"
      style="--grid-max-height: 500px;"
      @onEnableGridItem="${this.onEnableGridItem.bind(this)}"
      @onDisableGridItem="${this.onDisableGridItem.bind(this)}"
      @selectRows="${this.onSelectRows.bind(this)}"
    >
      <div class="header-buttons" slot="grid-header-actions-slot">${this.renderHeaderButtons()}</div>
    </gz-grid-view> `;
  }

  protected renderHeaderButtons(): TemplateResult {
    return html`
      <gz-button
        id="enable-button"
        type="${ButtonTypes.OUTLINE}"
        @click="${this.changeRulesEnabledStateOfSelectedItems.bind(this, true)}"
        >${translateFn('Enable')}
      </gz-button>

      <gz-button
        id="disable-button"
        type="${ButtonTypes.QUIET}"
        color="${ButtonColors.SECONDARY}"
        disabled
        @click="${this.changeRulesEnabledStateOfSelectedItems.bind(this, false)}"
        >${translateFn('Disable')}
      </gz-button>
    `;
  }

  private buildGrid(): void {
    // view id
    this.gridComponent.viewId = 'policy-user-groups-with-selection-grid';

    // column renderers
    this.gridComponent.columns = GRID_COLUMNS;
    this.gridComponent.allColumnDetails = getGridColumnDetails();
    this.gridComponent.columnRenderers = {
      [ColumnKeys.STATUS]: statusCellCustomRenderer,
    };

    this.gridComponent.isCompactViewChecked = true;

    // refers to custom actions
    this.gridComponent.showGlobalActions = true;

    // refers to reset view
    this.gridComponent.hideHeaderActions = true;
    this.gridComponent.defaultViewProps = {
      id: 'policy-user-groups-with-selection-grid',
      name: 'Default view',
      type: ViewType.DEFAULT,
      displayedColumns: GRID_COLUMNS,
      filters: FILTERS,
      pageSize: DEFAULT_PAGE_SIZE,
      compactView: true,
    };

    // filters settings
    this.gridComponent.filters = FILTERS;
    this.gridComponent.removeFilters = false;
    this.gridComponent.predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;

    // pagination settings
    this.gridComponent.hasPagination = true;
    this.gridComponent.inlineRowActions = this.getInlineRowActions();

    // data provider settings
    this.gridComponent.dataProvider = new GzExchangeUserGroupsGridDataProvider();

    // selection settings
    this.gridComponent.hasSelectableRows = true;
    this.gridComponent.selectionStoreConfig = {
      functionalMode: SelectionStoreMode.RECORDS,
      uniqueIdentifier: '_id',
      maxNumOfItems: SELECTION_STORE_CONFIGURATION.maxNumOfItems,
    };

    this.gridComponent.customStrings = {
      noSearchResultsString: translateFn(
        'No user groups defined. To create groups, go to {bold}Exchange Protection > General{/bold} in the policy settings.',
        {
          bold: '<strong>',
          '/bold': '</strong>',
        }
      ),
    };

    // set dynamic height
    this.gridComponent.dynamicHeight = true;

    if (!this.gridComponent.started) {
      this.gridComponent.injectDependencies({
        translateFn: translateFn,
      });
      this.gridComponent
        .start()
        .catch(console.error)
        .then(() => {
          const gzImageRepo = this.gridComponent.shadowRoot?.querySelector(
            '.no-results gz-image-repo'
          ) as GzImageRepoWebComponent;
          gzImageRepo.state = ImageState.NO_IMAGE;
        });
    }
  }

  private getInlineRowActions() {
    const inlineRowActions: Array<SelectOption> = [
      {
        title: translateFn('Enable'),
        value: PolicyGridEvents.OnEnableGridItem,
      },
      {
        title: translateFn('Disable'),
        value: PolicyGridEvents.OnDisableGridItem,
      },
    ];
    return inlineRowActions;
  }

  protected onSelectRows(event: CustomEvent) {
    this.selectedItemsGridData = event.detail.selectionStoreData;

    const isDisabled = !event.detail.selectionStoreData.length;
    this.enableButton.disabled = isDisabled;
    this.disableButton.disabled = isDisabled;
  }

  private changeRulesEnabledStateOfSelectedItems(enabled: boolean): void {
    if (!this.selectedItemsGridData.length) {
      return;
    }

    this.selectedItemsGridData.forEach((item: UserGroupUiData) =>
      enabled
        ? (this.gridComponent.dataProvider as GzExchangeUserGroupsGridDataProvider).addToSelected(item.specifics.guid)
        : (this.gridComponent.dataProvider as GzExchangeUserGroupsGridDataProvider).removeFromSelected(
            item.specifics.guid
          )
    );
    this.gridComponent.controller?.handleGridItemsLoad(true, true);
  }

  private onEnableGridItem(event: CustomEvent): void {
    (this.gridComponent.dataProvider as GzExchangeUserGroupsGridDataProvider).addToSelected(
      event.detail.rowData.item.specifics.guid
    );
    this.gridComponent.controller?.handleGridItemsLoad(true, true);
  }

  private onDisableGridItem(event: CustomEvent): void {
    (this.gridComponent.dataProvider as GzExchangeUserGroupsGridDataProvider).removeFromSelected(
      event.detail.rowData.item.specifics.guid
    );
    this.gridComponent.controller?.handleGridItemsLoad(true, true);
  }
}
